import { CodeCopy, Col, H2, Image, PortableText, Row, Section } from '@ahmdigital/ui';
import { useSelector } from 'react-redux';
import generatePortableText from '@ahmdigital/logic/lib/content/utils/generate-portable-text';
import styled from 'styled-components';

import EmptyOfferSpacer from '../empty-offer-spacer';
import getContentForOffer from '../../../tile/utils/get-content-for-offer';
import hasEndPunctuation from '../../../utils/has-end-punctuation';
import OfferBannerHeader from '../offer-header';
import offerTagImage from '../../../images/offer-tag.svg';
import selectReferralCode from '../../../../../selectors/select-referral-code';
import type { Offer, OfferPresaleText, OfferRewards, Placeholder } from '../../../types';
import type { OfferBannerVariant } from '../..';

type OfferBannerSectionProps = {
  $variant: OfferBannerVariant;
};

const OfferBannerSection = styled(Section)<OfferBannerSectionProps>`
  background: ${({ theme }) => theme.colors.purpleTint};
  padding: 1rem;

  ${({ $variant, theme }) =>
    $variant === 'inHero' &&
    `
      border-bottom: 1px solid ${theme.colors.purple};

      @media (min-width: ${theme.media.sm}) {
        border-left: 0.5rem solid ${theme.colors.black};
        border-right: 0.5rem solid ${theme.colors.black};
        margin-left: auto;
        margin-right: auto;
        max-width: ${theme.grid.wrapperWidthExtraExtraLarge};
      }
  `}

  ${({ $variant, theme }) =>
    $variant === 'sideBordered' &&
    `
      @media (min-width: ${theme.media.sm}) {
        border-left: 0.5rem solid ${theme.colors.black};
        border-right: 0.5rem solid ${theme.colors.black};
      }
  `}

  ${({ $variant, theme }) =>
    $variant === 'bordered' &&
    `
      border: 0.1rem solid ${theme.colors.purple};
  `}
`;

const CodeCopyInline = styled(CodeCopy)`
  display: inline-block;
`;

const StyledCol = styled(Col)`
  @media (max-width: ${({ theme }) => theme.media.sm}) {
    display: none;
  }
`;

const StyledSpan = styled.span`
  display: inline-block;
`;

// Reserves the same vertical space as the rendered banner so callers that mount
// above critical UI (e.g. the product header) don't shift while data resolves.
const EmptyStateWrapper = styled.div`
  border: 0.1rem solid transparent;
`;

type OfferBannerViewProps =
  | { empty: true }
  | {
      empty?: false;
      offer: Offer;
      placeholders: Placeholder[] | undefined;
      presaleEnd: OfferPresaleText | undefined;
      presaleSentence: OfferPresaleText | undefined;
      rewards: OfferRewards | undefined;
      variant: OfferBannerVariant;
    };

const OfferBannerView = (props: OfferBannerViewProps) => {
  const referralCode = useSelector(selectReferralCode);
  const { empty } = props;

  if (empty) {
    return (
      <EmptyStateWrapper>
        <EmptyOfferSpacer />
      </EmptyStateWrapper>
    );
  }

  const { offer, placeholders, presaleEnd, presaleSentence, rewards, variant } = props;
  const { ends, header, promoCode, subtext } = getContentForOffer(offer, referralCode);

  const headerText = generatePortableText(header)?.children?.[0]?.text;
  // adding whitespace between sentences
  const endOfSentence = hasEndPunctuation(headerText) ? ' ' : '. ';

  const allOfferCopy = {
    value: [...(subtext?.value ?? {}), { value: ' ' }, ...(ends?.value ?? {})],
  };
  const offerText =
    subtext && ends ? (
      <PortableText source={generatePortableText(allOfferCopy)} />
    ) : (
      <PortableText source={generatePortableText(subtext || ends)} />
    );
  const presaleTextSummary =
    presaleSentence && presaleEnd
      ? {
          value: [...(presaleSentence?.value ?? {}), { value: ' ' }, ...(presaleEnd?.value ?? {})],
        }
      : null;

  return (
    <OfferBannerSection id="offer-banner" $variant={variant}>
      <Row>
        <StyledCol df={1}>
          <Image src={offerTagImage.src} alt="Offer" />
        </StyledCol>
        <Col xs={12} df={11}>
          <H2 looksLike="h5">
            <OfferBannerHeader header={header} offer={offer} placeholders={placeholders} rewards={rewards} />
            {endOfSentence}
            <StyledSpan>Use {promoCode !== referralCode && 'our'} code:</StyledSpan>{' '}
            <CodeCopyInline asTextLink codeValue={promoCode as string} />
          </H2>
          {presaleTextSummary ? <PortableText source={generatePortableText(presaleTextSummary)} /> : offerText}
        </Col>
      </Row>
    </OfferBannerSection>
  );
};

export default OfferBannerView;
